import type {
	CardHandoverLocationOption,
	CreateEntryPointBody,
	EntryPoint,
	EntryPointOption,
	ListEntryPointsQueryInput,
	Paginated,
	PatchEntryPointBody,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { queryClient } from "./query-client";

/**
 * Entry points (M9): the airports, land crossings and ferry terminals people
 * come through, with the minutes transport timing uses. Game Settings edits
 * them (`/settings/entry-points`); the travel leg form picks from `/options`.
 */
export const entryPointKeys = {
	all: ["entry-points"] as const,
	list: (q: ListEntryPointsQueryInput) => ["entry-points", "list", q] as const,
	options: ["entry-points", "options"] as const,
};

/** `GET /entry-points` (ADMIN / SUPER_ADMIN) — one offset page + total in one statement. */
export const entryPointsListOptions = (q: ListEntryPointsQueryInput) =>
	queryOptions({
		queryKey: entryPointKeys.list(q),
		queryFn: ({ signal }) => api.get<Paginated<EntryPoint>>("/entry-points", { query: { ...q }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 60_000,
	});

/**
 * `GET /entry-points/options` — active ones in display order, for any signed-in
 * GMS role. Tens of rows that change a few times a season: cached long.
 */
export const entryPointOptionsOptions = () =>
	queryOptions({
		queryKey: entryPointKeys.options,
		queryFn: ({ signal }) => api.get<EntryPointOption[]>("/entry-points/options", { signal }),
		staleTime: 10 * 60_000,
	});

export const invalidateEntryPoints = () => queryClient.invalidateQueries({ queryKey: entryPointKeys.all });

export const createEntryPoint = (body: CreateEntryPointBody) => api.post<EntryPoint>("/entry-points", body);

export const updateEntryPoint = (id: string, body: PatchEntryPointBody) => api.patch<EntryPoint>(`/entry-points/${id}`, body);

/** Refused with 409 CONFLICT while travel uses it (`rethrowInUse`): deactivate instead. */
export const deleteEntryPoint = (id: string) => api.delete<null>(`/entry-points/${id}`);

/**
 * `GET /card-handover-locations/options` — the welcome-desk picker in the
 * entry point form. (Kept here, not in `lib/handover-locations/queries.ts`,
 * which no page needed options from until now; same key shape so a later
 * move is a rename.)
 */
export const handoverLocationOptionsOptions = () =>
	queryOptions({
		queryKey: ["card-handover-locations", "options"] as const,
		queryFn: ({ signal }) => api.get<CardHandoverLocationOption[]>("/card-handover-locations/options", { signal }),
		staleTime: 10 * 60_000,
	});
